// Who is signed in, for the whole app. Asked once at start and again after every sign-in, sign-out
// or change to the account, so pages never hold their own copy of the user.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { auth, type Me, type Providers, type UserInfo } from './api'

interface AuthState {
  /** Undefined until the first answer from the server. */
  me: Me | undefined
  user: UserInfo | null
  providers: Providers
  refresh: () => Promise<Me | undefined>
  /** Takes a fresh copy of the user, as the account endpoints return one, without a round trip. */
  setUser: (user: UserInfo) => void
  signOut: () => Promise<void>
}

const noProviders: Providers = { google: false, microsoft: false, sms: false, passkey: false }

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | undefined>(undefined)

  const refresh = useCallback(async () => {
    try {
      const next = await auth.me()
      setMe(next)
      return next
    } catch {
      // the server is unreachable or the store is not open yet: treat as signed out, keep what we know about providers
      const next: Me = { user: null, providers: me?.providers ?? noProviders }
      setMe(next)
      return next
    }
  }, [me?.providers])

  useEffect(() => {
    void refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, at start
  }, [])

  const setUser = useCallback((user: UserInfo) => {
    setMe((current) => ({ user, providers: current?.providers ?? noProviders }))
  }, [])

  const signOut = useCallback(async () => {
    await auth.logout()
    setMe((current) => ({ user: null, providers: current?.providers ?? noProviders }))
  }, [])

  const value = useMemo<AuthState>(
    () => ({ me, user: me?.user ?? null, providers: me?.providers ?? noProviders, refresh, setUser, signOut }),
    [me, refresh, setUser, signOut],
  )
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const state = useContext(AuthContext)
  if (!state) throw new Error('useAuth needs an <AuthProvider> above it')
  return state
}
